// Sekce hodnocení (souhrn skóre + odznaky + řádky platforem + citace zákazníků).
//
// Vznikla jako poslední sekce homepage (`design_handoff_homepage/README.md` §6);
// od 2026-09-05 (zadání Tomáše) ji nese PATIČKA, takže stojí na každé stránce
// webu — na homepage na stejném místě jako dřív (patička hned následuje za
// obsahem), jinde nad pásem Instagramu. Pokladna (`/kosik/`, `/objednavka/*`)
// patičku nerenderuje a hodnocení tam vědomě není: má vlastní trust pás
// a druhý blok důvěry by tunel jen prodloužil.
//
// Recenze U2 (mock schválený Tomášem 6. 10. 2026, plátno H8uCHzSqpDc7pGLKVKyswp,
// řada „Patička · sekce hodnocení (U2)", parita tools/audit/reviews-footer-parity.mjs):
//   - řádky platforem v NATIVNÍ metrice (Heureka a Zboží.cz procenta, ostatní
//     hvězdy), řádek Jipos.cz (hodnocení z webu), poznámka pod Firmy.cz (sdílí
//     hodnocení se Zboží.cz) a Jipos.cz; souhrn z finálních řádků bez Firmy.cz
//     a věta o převodu procent na hvězdy (reviewsModel.js);
//   - citace podle typu stránky z `/reviews/context` (téma stránky, recenze
//     produktů ve výpisu kategorie), nejdřív ověřené Heurekou; nadpis řádku
//     citací a vpravo odkaz „Jak recenze ověřujeme" (sekce stránky hodnocení,
//     `reviewsVerify.js`);
//   - data: manifest (`reviews.platforms`, `reviews.quotes`) je ZÁLOHA a sekce
//     se z něj staví hned; s přepínačem `reviewsApi` dorazí `/reviews/summary`
//     a citace a sekce se vymění za verzi s živými čísly (stejný tvar, jiná data).
//     Bez odpovědi API (výpadek, SK bez dat, offline fixtures) zůstává záloha.
//
// Názvy platforem nesou LOGA (sprite `logos.svg`, logo.js), text zůstává
// ve skrytém `.bs-rv__plat-text` a v `aria-label` odkazu.

import { icon } from './icon.js';
import { logo, hasLogo } from './logo.js';
import { number } from './money.js';
import SHOP from '../../config/shop.js';
import { page, byPage } from './pages.js';
import { featureOn } from './features.js';
import { reviewsContext, reviewsSummary } from './searchApi.js';
import { verifyHref } from './reviewsVerify.js';
import {
  CONTEXT_LIMIT,
  QUOTES_SHOWN,
  decimal,
  formatDate,
  pageTopic,
  pickQuotes,
  platformRows,
  quotePlan,
  quoteText,
  starsOf,
  summarize,
} from './reviewsModel.js';
import L from '../locale/cs/reviews.js';

// Odznaky třetích stran (zadání Tomáše 2026-09-09, `reviews.badges` v manifestu
// shopu): Heureka „Ověřeno zákazníky", Firmy.cz „TOP firma 2025", Retino
// „Bezproblémové reklamace". Stojí ve STŘEDNÍM sloupci sekce mezi souhrnem
// a řádky platforem, rozložení 2 + 1 (Heureka a Firmy.cz v řadě, Retino pod
// nimi na šířku řady).
//
// ⚠️ Vědomě STATICKÉ obrázky s odkazem, ne oficiální embed skripty: Heureka
// `gjs.php?n=wdgt` i Retino `badgeInjector.js` rendrují nakonec tentýž
// `<a><img></a>` (ověřeno 2026-09-09 standalone renderem), ale Retino navíc
// zakládá cookie `__rbs` a střílí tracking pixel na AWS s hostname + href —
// to by bez souhlasu na stránku nepatřilo. Statická cesta = žádný cizí skript,
// žádná cookie, rozměry známé dopředu (slot má pevnou velikost, obrázek
// nezpůsobí shift). Odkazy vedou tam, kam by vedl embed.
export const BADGES = SHOP.reviews.badges;

// Má shop vůbec nějakou hodnoticí platformu? (`reviews.platforms` v manifestu.)
// Bez ní se sekce hodnocení, trust karta přihlášení ani dlaždice „Ověřený
// obchod" v pokladně nestaví — čísla by neměla odkud být.
export const HAS_REVIEWS = SHOP.reviews.platforms.length > 0;

// Témata citací podle stránky (cesta z manifestu `pages` → téma `/reviews/context`).
// Prodejna téma nemá (14 textů, sonda 6. 10.), výpis kategorie poznává pageTopic().
const TOPIC_PAGES = byPage({
  shipping: 'doprava',
  orderStatus: 'doprava',
  claim: 'reklamace',
  claimsPolicy: 'reklamace',
  withdrawal: 'reklamace',
  service: 'reklamace',
  contact: 'komunikace',
  faq: 'komunikace',
  b2b: 'komunikace',
});

// ---------------------------------------------------------------------------
// Helpery (kopie z homepage.js — moduly si je drží samy, ať nevzniká závislost)
// ---------------------------------------------------------------------------

function node(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function link(href, className, text) {
  const a = node('a', className, text);
  a.href = href;
  return a;
}

// Formát čísla (tisíce s nezlomitelnou mezerou) jde přes sdílený `number()`
// z money.js — formátuje v jazyce shopu (SHOP.shop.language), ne natvrdo 'cs-CZ'.
const nbsp = (n) => number(Math.round(n));

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

/** Záloha z manifestu: řádky platforem a citace bez API (staví se hned). */
function fallback() {
  return {
    rows: platformRows(SHOP.reviews.platforms, null),
    plan: quotePlan({ fallback: SHOP.reviews.quotes }),
  };
}

function shoptetData() {
  try {
    const first = window.dataLayer && window.dataLayer[0];
    return (first && first.shoptet) || null;
  } catch (e) {
    return null;
  }
}

function itemsOf(res) {
  return res && Array.isArray(res.items) ? res.items : [];
}

let loading = null;

/**
 * Živá data sekce (jednou na stránku, sdílí je patička, trust karta přihlášení
 * a karta recenzí na detailu): `{rows, plan, shop, obecne}`, nebo null = nic
 * nového proti záloze (přepínač `reviewsApi` vypnutý, vrstva hledání neběží, API
 * nic nevrátilo). Citace tématu nebo kategorie, kterých je pod tři, doplní obecné
 * citace obchodu. `shop` = hodnocení obchodu celkem ze `/reviews/summary`,
 * `obecne` = obecné citace obchodu z API (karta recenzí z nich bere ty, které
 * patička neukazuje: `plan.quotes`, reviewHighlights.js `shopQuotes`).
 */
export function loadReviews() {
  if (loading) return loading;
  if (!HAS_REVIEWS || !featureOn('reviewsApi')) {
    loading = Promise.resolve(null);
    return loading;
  }
  const ctx = pageTopic(location.pathname, shoptetData(), TOPIC_PAGES);
  const category = ctx.category ? ctx.category.guid : '';
  loading = Promise.all([reviewsSummary(), reviewsContext(ctx.topic, category, CONTEXT_LIMIT)])
    .then(function (res) {
      const primary = itemsOf(res[1]);
      const needGeneral = ctx.topic !== 'obecne' && pickQuotes(primary).length < QUOTES_SHOWN;
      const general = needGeneral ? reviewsContext('obecne', '', CONTEXT_LIMIT) : Promise.resolve(null);
      return general.then(function (gen) {
        const rows = platformRows(SHOP.reviews.platforms, res[0]);
        const plan = quotePlan({
          topic: ctx.topic,
          category: ctx.category,
          primary: primary,
          general: itemsOf(gen),
          fallback: SHOP.reviews.quotes,
        });
        const fresh = rows.some((r) => r.source === 'api') || plan.source === 'api';
        if (!fresh) return null;
        const summary = res[0];
        return {
          rows: rows,
          plan: plan,
          shop: summary && summary.shop ? summary.shop : null,
          obecne: ctx.topic === 'obecne' ? primary : itemsOf(gen),
        };
      });
    })
    .catch(function () {
      return null;
    });
  return loading;
}

/**
 * Souhrn hodnocení v HOTOVÉM tvaru pro místa mimo sekci `.bs-rv` (trust karta
 * panelu na /login/ a /registrace/). Jeden zdroj čísel = tytéž řádky a týž
 * součet jako sekce (zadání Tomáše 2026-09-10; do té doby měla karta zapsané
 * „4,8/5" a „3 012 recenzí", zatímco sekce počítala 4,6 a 23 783). `rows`
 * z `loadReviews()`, bez nich záloha z manifestu.
 *
 * @returns {{score:string, count:string, percent:string, max:string, suffix:string, customers:string}|null}
 *   null, když žádná platforma nemá čísla (sekce se v tom případě taky nestaví)
 */
export function reviewSummary(rows) {
  const s = summarize(rows || fallback().rows);
  if (!s) return null;
  return {
    score: decimal(s.stars),
    count: nbsp(s.count),
    percent: ((s.stars / 5) * 100).toFixed(1) + '%',
    max: L.max,
    suffix: L.countSuffix,
    customers: SHOP.reviews.customers ? nbsp(SHOP.reviews.customers) + ' ' + L.customersLabel : '',
  };
}

function sourceLine(names) {
  if (names.length < 2) return L.sourceSingle + names[0];
  return L.sourceMulti + names.join(' · ');
}

/** Řádky platforem ze zálohy v manifestu (staví se hned, než dorazí API). */
export function manifestRows() {
  return fallback().rows;
}

/**
 * Texty souhrnu `s` (z `summarize`): počet, zdrojový řádek a věta o převodu
 * procent (null, když v součtu žádná procenta nejsou). Sdílí je patička
 * a souhrn na stránce hodnocení (ratingsPage.js), ať čtou stejně.
 */
export function summaryLines(s) {
  let converted = null;
  // procenta převedená na hvězdy souhrn říká (rozhodnutí 6. 10.: nepřevádět tiše)
  if (s.converted) {
    const first = s.pct[0];
    const example = L.convertedExample(Math.round(first.value), decimal(starsOf(first)));
    converted = L.converted(s.pct.map((r) => r.name), example);
  }
  return { count: nbsp(s.count) + ' ' + L.countSuffix, source: sourceLine(s.names), converted: converted };
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

// Název platformy = logo ze spritu; textová varianta zůstává v DOM pro čtečky
// a pro případ, že by sprite nedorazil (`.bs-rv__plat-text` schová CSS jen
// tehdy, když logo skutečně je).
function platformName(p) {
  const wrap = node('span', 'bs-rv__plat-name');
  const mark = hasLogo(p.logo) ? logo(p.logo, 'bs-rv__plat-logo') : null;
  if (mark) {
    wrap.classList.add('is-logo');
    wrap.appendChild(mark);
  }
  wrap.appendChild(node('span', 'bs-rv__plat-text', p.name));
  return wrap;
}

function platformNote(p, rows) {
  if (p.own) return L.ownNote;
  if (!p.pool) return null;
  const host = rows.find((r) => r.key === p.pool);
  return host ? L.poolNote(host.name) : null;
}

/**
 * Řádek platformy (`a.bs-rv__plat`, bez odkazu `span`). Patička i souhrn na
 * stránce hodnocení; `ownHref` = kam vede řádek webu shopu (na stránce hodnocení
 * na výpis pod souhrnem, jinde na stránku hodnocení).
 */
export function platformRow(p, rows, ownHref) {
  const pct = p.metric === 'pct';
  const href = p.own ? ownHref || page('ratings') : p.href;
  const row = href ? link(href, 'bs-rv__plat') : node('span', 'bs-rv__plat');
  const count = nbsp(p.count) + ' ' + L.countSuffix;
  const note = platformNote(p, rows);
  if (row.tagName === 'A') {
    // profily na cizích portálech se otevírají v novém okně (zadání 2026-08-31)
    if (/^https?:/.test(href)) {
      row.target = '_blank';
      row.rel = 'noopener';
    }
    const score = pct ? L.ariaPercent(Math.round(p.value)) : L.ariaStars(decimal(p.value));
    row.setAttribute('aria-label', L.platformAria(p.name, score, count) + (note ? ', ' + note : ''));
  }
  row.appendChild(platformName(p));
  row.appendChild(node('span', 'bs-rv__plat-count', count));
  const sc = node('span', 'bs-rv__plat-score', pct ? String(Math.round(p.value)) : decimal(p.value));
  sc.appendChild(node('span', 'bs-rv__plat-max', pct ? L.percent : L.platMax));
  row.appendChild(sc);
  if (row.tagName === 'A') row.appendChild(icon('arrow-right', 'bs-rv__plat-chev'));
  if (note) row.appendChild(node('span', 'bs-rv__plat-note', note));
  return row;
}

function badgeLink(b) {
  const a = link(b.href, 'bs-rv__badge bs-rv__badge--' + b.key);
  a.target = '_blank';
  a.rel = 'noopener';
  const img = document.createElement('img');
  img.src = b.src;
  img.alt = b.alt;
  img.width = b.w;
  img.height = b.h;
  img.loading = 'lazy';
  img.decoding = 'async';
  a.appendChild(img);
  return a;
}

// Sloupec odznaků: řada Heureka + Firmy.cz, pod ní Retino přes šířku řady.
// Šířku řady i Retina počítá CSS z `--bs-rvb-h` (výška řady) a poměrů stran,
// které sem JS předá jako custom properties — obrázky tak mají místo ještě
// před načtením.
function buildBadges() {
  const col = node('div', 'bs-rv__badges');
  const row = node('div', 'bs-rv__badges-row');
  BADGES.forEach(function (b) {
    col.style.setProperty('--bs-rvb-' + b.key, (b.w / b.h).toFixed(4));
  });
  BADGES.filter((b) => b.key !== 'retino').forEach((b) => row.appendChild(badgeLink(b)));
  col.appendChild(row);
  const retino = BADGES.find((b) => b.key === 'retino');
  if (retino) col.appendChild(badgeLink(retino));
  return col;
}

function buildSummary(s) {
  const sum = node('div', 'bs-rv__sum');
  sum.appendChild(node('span', 'bs-rv__eyebrow', L.eyebrow));
  const score = node('span', 'bs-rv__score');
  score.appendChild(node('span', 'bs-rv__score-num', decimal(s.stars)));
  score.appendChild(node('span', 'bs-rv__score-max', L.max));
  sum.appendChild(score);
  const lines = summaryLines(s);
  sum.appendChild(node('span', 'bs-rv__count', lines.count));
  sum.appendChild(node('span', 'bs-rv__src', lines.source));
  if (lines.converted) sum.appendChild(node('span', 'bs-rv__conv', lines.converted));
  const cta = link(page('ratings'), 'bs-btn bs-rv__cta', L.ctaLabel);
  cta.appendChild(icon('arrow-right'));
  sum.appendChild(cta);
  return sum;
}

/** Hvězdy citace: plné glyfy + šedé zbylé (jako karta recenzí na detailu). */
function quoteStars(rating) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  const el = node('span', 'bs-rv__quote-stars', '★'.repeat(n));
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', L.starsAria(n));
  if (n < 5) el.appendChild(node('span', 'bs-rv__quote-stars-off', '★'.repeat(5 - n)));
  return el;
}

// Štítek původu (stejná pravidla jako karta recenzí na detailu, U3): import
// z dotazníku Heureky = „Ověřeno Heurekou" se štítem, web i neznámý původ =
// šedé „Recenze na Jipos.cz" (nákup u nich neověřujeme).
function quoteSource(q) {
  if (q.source === 'heureka') {
    const el = node('span', 'bs-rv__quote-src bs-rv__quote-src--heureka');
    el.appendChild(icon('shield'));
    el.appendChild(node('span', null, L.sourceHeureka));
    return el;
  }
  return node('span', 'bs-rv__quote-src', L.sourceWeb(SHOP.shop.name));
}

function buildQuote(q) {
  const item = node('figure', 'bs-rv__quote');
  item.appendChild(quoteStars(q.rating));
  // recenze produktu (výpis kategorie): název produktu odkazem na detail
  const product = q.product;
  if (product && product.name && typeof product.path === 'string' && product.path.charAt(0) === '/') {
    item.appendChild(link(product.path, 'bs-rv__quote-product', product.name));
  }
  item.appendChild(node('blockquote', 'bs-rv__quote-text', quoteText(q)));
  const by = node('figcaption', 'bs-rv__quote-by');
  by.appendChild(quoteSource(q));
  const date = formatDate(q.date);
  if (date) {
    const sep = node('span', 'bs-rv__quote-sep', '·');
    sep.setAttribute('aria-hidden', 'true');
    by.appendChild(sep);
    by.appendChild(node('span', 'bs-rv__quote-date', date));
  }
  item.appendChild(by);
  return item;
}

function quotesTitle(plan) {
  if (plan.title === 'kategorie') {
    return plan.category && plan.category.name ? L.categoryTitle(plan.category.name) : L.quotesTitle.obecne;
  }
  return L.quotesTitle[plan.title] || L.quotesTitle.obecne;
}

// Citáty jsou doklad, ne obsah — bez karet a rámů (handoff §6). Nadpis říká,
// o čem jsou (téma stránky), odkaz „Jak recenze ověřujeme" je u nich (§ 5a
// odst. 5 ZOS: informace o ověřování patří k zobrazeným recenzím).
function buildVoice(plan) {
  if (!plan.quotes.length) return null;
  const voice = node('div', 'bs-rv__voice');
  voice.appendChild(node('h3', 'bs-rv__voice-title', quotesTitle(plan)));
  const list = node('div', 'bs-rv__quotes');
  plan.quotes.forEach(function (q) {
    list.appendChild(buildQuote(q));
  });
  voice.appendChild(list);
  const verify = verifyHref();
  if (verify) voice.appendChild(link(verify, 'bs-rv__verify', L.verify));
  return voice;
}

/**
 * Celá sekce hodnocení (souhrn · odznaky · platformy, pod tím citáty).
 * Vrací `<section class="bs-rv">` s vnitřním `.bs-rv__in.container` (sekce je
 * celoplošný pás — dřív to na homepage řešil mixin `zone` uvnitř containeru,
 * v patičce by neměl kolem co roztahovat). Bez jediného skóre sekci nestavíme.
 * Bez argumentů = záloha z manifestu.
 */
export function buildReviews(data) {
  const d = data || fallback();
  const s = summarize(d.rows);
  if (!s) return null;

  const section = node('section', 'bs-rv');
  section.dataset.bsSource = d.rows.some((r) => r.source === 'api') || d.plan.source === 'api' ? 'api' : 'manifest';
  const inner = node('div', 'bs-rv__in container');
  section.appendChild(inner);

  // Vodoznak: vyplněná část = souhrnné skóre z 5
  const wm = node('span', 'bs-rv__wm', '★★★★★');
  wm.setAttribute('aria-hidden', 'true');
  const fill = node('span', 'bs-rv__wm-fill', '★★★★★');
  fill.style.width = ((s.stars / 5) * 100).toFixed(1) + '%';
  wm.appendChild(fill);
  inner.appendChild(wm);

  const grid = node('div', 'bs-rv__grid');
  inner.appendChild(grid);
  grid.appendChild(buildSummary(s));
  grid.appendChild(buildBadges());
  const plats = node('div', 'bs-rv__plats');
  d.rows.forEach(function (p) {
    plats.appendChild(platformRow(p, d.rows));
  });
  grid.appendChild(plats);

  const voice = buildVoice(d.plan);
  if (voice) inner.appendChild(voice);
  return section;
}

/**
 * Sekce do patičky: hned ze zálohy (manifest), po dorazu živých dat z API ji
 * vymění za verzi s nimi. Vrací vloženou sekci, nebo null (shop bez platforem).
 */
export function mountReviews(footer) {
  const first = buildReviews();
  if (!first) return null;
  footer.insertBefore(first, footer.firstChild);
  loadReviews().then(function (live) {
    if (!live || !first.isConnected) return;
    const next = buildReviews(live);
    if (next) first.replaceWith(next);
  });
  return first;
}
